import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Typography,
  CircularProgress,
  Alert,
  Box,
  Chip,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  Business as BusinessIcon,
  CheckCircle as CheckIcon,
  Group as GroupIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { axiosService } from 'shared/helpers/axios';
import { useUser } from 'shared/helpers/axios';

function InviteToConsortiumDialog({ open, onClose, user, onDone }) {
  const { user: currentUser } = useUser();
  const [consortia, setConsortia] = useState([]);
  const [loading, setLoading] = useState(false);
  const [inviting, setInviting] = useState({});
  const [invitations, setInvitations] = useState({});
  const [isMember, setIsMember] = useState({});
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  useEffect(() => {
    if (open && currentUser?.id && user?.id) {
      
      fetchUserConsortia();
      
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, currentUser?.id, user?.id]);

  const fetchUserConsortia = async () => {
    setLoading(true);
    setError(null);
    try {
      // console.log('Fetching consortia for user:', currentUser?.id);
      const response = await axiosService.get('/consortium/consortiums/by-user/');
      // console.log('Consortia response:', response?.data);
      
      // Handle both array and paginated responses
      const data = Array.isArray(response?.data) 
        ? response.data 
        : response.data?.results || [];
    
      setConsortia(
          data.filter( (con) =>  con?.coordinator_member === currentUser.id)
        );
      
      // Check both membership and invitation status
      if (data.length > 0) {
        // console.log('Starting membership and invitation checks for', data.length, 'consortia');
        await Promise.all([
          checkMembershipStatus(data),
          fetchInvitations(data.map(c => c.id))
        ]);
        // console.log('Membership and invitation checks complete');
      } else {
        console.log('No consortia to check');
      }
    } catch (err) {
      console.error('Error fetching consortia:', err);
      setError('Failed to load your consortia.');
    } finally {
      setLoading(false);
    }
  };


  const fetchInvitations = async (consortiumIds) => {
    try {
      console.log('Bulk checking invitations for consortium IDs:', consortiumIds);
      
      if (!consortiumIds?.length || !user?.id) {
        setInvitations({});
        return;
      }
      
      const response = await axiosService.get('/interact/consortium-invitations/bulk-check-user-in-consortia/', {
        params: {
          consortium_ids: consortiumIds.join(','),
          user_id: user.id,
          // include_details: false  // Set to true if you need invitation details
        }
      });
      
      
      // Convert array response to map
      const invitationMap = response?.data;
      // console.log('#### Bulk invitation check result:', invitationMap );
      // console.log('Bulk invitation check result:', invitationMap);
      setInvitations(invitationMap);
      
    } catch (error) {
      console.error('Error in bulk invitation check:', error);
      // Fallback to individual requests
      // await fetchInvitationsFallback(consortiumIds);
    }
  };


  const ensureArray = (value) => {
    if (Array.isArray(value)) return value;
    if (value && typeof value === 'object') return [value];
    return [];
  };

  const checkMembershipStatus = (consortiaList) => {
    // console.log('checkMembershipStatus called with:', consortiaList);
    const membershipStatus = {};
    
    consortiaList.forEach((consortium) => {
      // console.log(`Checking membership for consortium ${consortium.id}:`, consortium);
      
      const memberships = ensureArray(consortium.memberships);
      // console.log(`Memberships array:`, memberships);
      
      // Check if the invited user is already a member
      const isAlreadyMember = memberships.some((membership) => {
        const memberId = membership.member?.id || membership.member;
        const memberIdStr = String(memberId);
        const userIdStr = String(user?.id);
        
        // console.log(`Comparing member: ${memberIdStr} with user: ${userIdStr}`, memberIdStr === userIdStr);
        
        return memberIdStr === userIdStr;
      });
      
      membershipStatus[consortium.id] = isAlreadyMember;
    });
    
    // console.log('Final membership status:', membershipStatus);
    setIsMember(membershipStatus);
  };

  const handleInvite = async (consortiumId) => {
    // Check if already invited from fetched data
    if (invitations[consortiumId]) {
      setError('User has already been invited to this consortium');
      setTimeout(() => setError(null), 3000);
      return;
    }
  
    setInviting((prev) => ({ ...prev, [consortiumId]: true }));
    setError(null);
    setSuccess(null);
  
    try {
      await axiosService.post(`/interact/consortiums/${consortiumId}/invitations/`, {
        recipient: user?.id,
        consortium: consortiumId,
      });
  
      // Update local state to mark as invited
      setInvitations(prev => ({ ...prev, [consortiumId]: true }));
      setSuccess(`Invitation sent successfully!`);
      onDone?.();
      
      // Auto-clear success message after 3 seconds
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error('Error sending invitation:', err);
      
      // Handle specific error cases
      if (err.response?.status === 400) {
        const errorMsg = err.response?.data?.recipient?.[0] || 
                        err.response?.data?.consortium?.[0] ||
                        err.response?.data?.detail || 
                        'User may already be invited or a member';
        setError(errorMsg);
        
        // If it's a duplicate invitation error, update local state
        if (errorMsg.includes('already') || errorMsg.includes('duplicate')) {
          setInvitations(prev => ({ ...prev, [consortiumId]: true }));
        }
      } else {
        setError('Failed to send invitation');
      }
    } finally {
      setInviting((prev) => ({ ...prev, [consortiumId]: false }));
    }
  };

  const handleClose = (e) => {
    setInvitations({});
    setIsMember({});
    setError(null);
    setSuccess(null);
    onClose(e);
  };

  const getButtonState = (consortiumId) => {
    if (isMember[consortiumId]) {
      return {
        label: 'Member',
        color: 'success',
        variant: 'outlined',
        disabled: true,
        icon: <CheckIcon />,
        tooltip: 'User is already a member',
      };
    }
    if (invitations[consortiumId]) {
      return {
        label: 'Invited',
        color: 'success',
        variant: 'outlined',
        disabled: true,
        icon: <CheckIcon />,
        tooltip: 'Invitation sent',
      };
    }
    if (inviting[consortiumId]) {
      return {
        label: 'Inviting...',
        color: 'primary',
        variant: 'contained',
        disabled: true,
        icon: <CircularProgress size={16} />,
        tooltip: 'Sending invitation',
      };
    }
    return {
      label: 'Invite',
      color: 'primary',
      variant: 'contained',
      disabled: false,
      icon: null,
      tooltip: 'Send invitation',
    };
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      onClick={(e) =>  {e.stopPropagation()}}
      sx={{zIndex: 1600}}
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <GroupIcon color="primary" />
            <Typography variant="h6">
              Invite {user?.first_name || user?.username || 'User'} to Consortium
            </Typography>
          </Box>
          <IconButton onClick={handleClose} size="small" aria-label="Close dialog">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
            {success}
          </Alert>
        )}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : consortia.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography variant="body1" color="text.secondary" gutterBottom>
              Consortium invitations must be performed by the coordinators. 
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Please either suggest a consortium for this user or contact your coordinator(s).
            </Typography>
          </Box>
        ) : (
          <>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Select a consortium to send an invitation to {user?.first_name || user?.username}:
            </Typography>
            <List>
              {consortia.map((consortium) => {
                const buttonState = getButtonState(consortium.id);
                // console.log('Rendering consortium:', consortium.id, consortium.name, buttonState);
                
                return (
                  <ListItem
                    key={consortium.id}
                    sx={{
                      border: 1,
                      borderColor: 'divider',
                      borderRadius: 1,
                      mb: 1,
                      bgcolor: 'background.paper',
                    }}
                  >
                    <ListItemAvatar>
                      <Avatar sx={{ bgcolor: 'primary.light' }} alt={consortium.name || 'Consortium'}>
                        <BusinessIcon />
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="subtitle1">
                            {consortium.name}
                          </Typography>
                          {consortium.status && (
                            <Chip
                              label={consortium.status}
                              size="small"
                              color={consortium.status === 'active' ? 'success' : 'default'}
                              sx={{ height: 20 }}
                            />
                          )}
                        </Box>
                      }
                      secondary={
                        <Typography variant="body2" color="text.secondary" noWrap>
                          {consortium.description || 'No description'}
                        </Typography>
                      }
                    />
                    <Tooltip title={buttonState.tooltip}>
                      <span>
                        <Button
                          variant={buttonState.variant}
                          color={buttonState.color}
                          size="small"
                          onClick={() => handleInvite(consortium.id)}
                          disabled={buttonState.disabled}
                          startIcon={buttonState.icon}
                          sx={{ minWidth: 100 }}
                        >
                          {buttonState.label}
                        </Button>
                      </span>
                    </Tooltip>
                  </ListItem>
                );
              })}
            </List>
          </>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}

export default InviteToConsortiumDialog;